/* David Pladen — homepage sections (sections/dp-hero, dp-bestsellers,
 * dp-looks, dp-inside, dp-home-reviews). Behaviour ported from main#top in
 * reference/demo.html:
 * - Hero video: plays muted unless the visitor prefers reduced motion; the
 *   round button pauses and plays it.
 * - Carousels ([data-dp-rail]): previous/next buttons, disabled at the ends,
 *   optional progress bar and pagination dots ([data-rail-dots]).
 * - Bestseller tabs: switch between collection tracks (ARIA tabs).
 * - "What's inside": hovering or focusing a callout highlights its line;
 *   on mobile the callouts scroll with dots.
 * Markup always comes from Liquid; this file only switches states.
 */

(() => {
  if (window.DpHomeLoaded) return;
  window.DpHomeLoaded = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = () => (reduceMotion() ? 'auto' : 'smooth');

  /* ---- Hero video ---- */
  const initHero = (root) => {
    const video = root.querySelector('video');
    const button = root.querySelector('[data-hero-toggle]');
    if (!video) return;
    video.muted = true;
    const setState = (playing) => {
      if (!button) return;
      button.textContent = playing ? button.dataset.pauseIcon : button.dataset.playIcon;
      button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
    };
    if (reduceMotion()) {
      video.pause();
      setState(false);
    } else {
      const play = video.play();
      if (play && play.catch) play.catch(() => setState(false));
      setState(true);
    }
    button?.addEventListener('click', () => {
      if (video.paused) {
        video.play();
        setState(true);
      } else {
        video.pause();
        setState(false);
      }
    });
  };

  /* ---- Carousels ---- */
  const updateRail = (rail) => {
    const root = rail.closest('[data-dp-rail-root]');
    if (!root) return;
    const max = rail.scrollWidth - rail.clientWidth;
    root.querySelectorAll(`[data-rail-dir="-1"]`).forEach((b) => {
      b.disabled = rail.scrollLeft < 4;
    });
    root.querySelectorAll(`[data-rail-dir="1"]`).forEach((b) => {
      b.disabled = rail.scrollLeft >= max - 4;
    });
    const bar = root.querySelector('[data-rail-bar]');
    if (bar && rail.scrollWidth) {
      bar.style.width = `${Math.min(100, (rail.clientWidth / rail.scrollWidth) * 100)}%`;
      bar.style.left = `${(rail.scrollLeft / rail.scrollWidth) * 100}%`;
    }
    const dots = root.querySelector('[data-rail-dots]');
    if (dots) {
      const count = rail.children.length;
      if (dots.children.length !== count) dots.innerHTML = '<i></i>'.repeat(count);
      const item = rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const step = item ? item.offsetWidth + gap : rail.clientWidth;
      const index = rail.scrollLeft >= max - 4 ? count - 1 : Math.round(rail.scrollLeft / step);
      Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('on', i === index));
    }
  };

  const activeRail = (root) => Array.from(root.querySelectorAll('[data-dp-rail]')).find((r) => !r.hidden);

  const initRails = (root) => {
    root.querySelectorAll('[data-dp-rail]').forEach((rail) => {
      rail.addEventListener('scroll', () => updateRail(rail), { passive: true });
      updateRail(rail);
    });
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rail-dir]');
      if (!button) return;
      const rail = activeRail(root);
      if (!rail) return;
      const item = rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const step = rail.dataset.railStep === 'page' ? rail.clientWidth * 0.9 : (item ? item.offsetWidth + gap : rail.clientWidth);
      rail.scrollBy({ left: parseInt(button.dataset.railDir, 10) * step, behavior: behavior() });
    });
  };

  /* ---- Bestseller tabs ---- */
  const initTabs = (root) => {
    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;
    const select = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      const all = root.querySelector('[data-all-link]');
      if (all && tab.dataset.url) all.href = tab.dataset.url;
      const rail = activeRail(root);
      if (rail) updateRail(rail);
    };
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: 1, ArrowLeft: -1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const next = tabs[(tabs.indexOf(tab) + keys[event.key] + tabs.length) % tabs.length];
        next.focus();
        select(next);
      });
    });
  };

  /* ---- What's inside ---- */
  const initInside = (root) => {
    const desktop = () => window.matchMedia('(min-width: 981px)').matches;
    const set = (h) => {
      root.classList.toggle('focus', !!h);
      root.querySelectorAll('[data-h]').forEach((el) => el.classList.toggle('on', el.dataset.h === h));
    };
    root.querySelectorAll('.sl').forEach((callout) => {
      callout.addEventListener('mouseenter', () => {
        if (desktop()) set(callout.dataset.h);
      });
      callout.addEventListener('focus', () => set(callout.dataset.h));
      callout.addEventListener('blur', () => {
        if (desktop()) set(null);
      });
    });
    root.querySelector('.spec-stage')?.addEventListener('mouseleave', () => {
      if (desktop()) set(null);
    });

    // Mobile: the callout row scrolls; dots and the highlighted card follow
    const row = root.querySelector('.sl-row');
    const dots = Array.from(root.querySelectorAll('.sl-dots i'));
    if (!row || !dots.length) return;
    const cards = Array.from(row.querySelectorAll('.sl'));
    const sync = () => {
      if (desktop()) return;
      const center = row.scrollLeft + row.clientWidth / 2;
      let best = 0;
      cards.forEach((card, i) => {
        const mid = card.offsetLeft + card.offsetWidth / 2;
        if (Math.abs(mid - center) < Math.abs(cards[best].offsetLeft + cards[best].offsetWidth / 2 - center)) best = i;
      });
      dots.forEach((d, i) => d.classList.toggle('on', i === best));
      cards.forEach((c, i) => c.classList.toggle('on', i === best));
    };
    row.addEventListener('scroll', () => window.requestAnimationFrame(sync), { passive: true });
    sync();
  };

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-dp-hero]').forEach(initHero);
    scope.querySelectorAll('[data-dp-rail-root]').forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = 'true';
      initRails(root);
      initTabs(root);
    });
    scope.querySelectorAll('[data-dp-inside]').forEach(initInside);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  window.addEventListener(
    'resize',
    () => document.querySelectorAll('[data-dp-rail]').forEach(updateRail),
    { passive: true }
  );
})();